import { PlusOutlined } from '@ant-design/icons';
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { httpCreators } from '@ppvrbh/merchant-frontend-core';
import { RBHAlert, RBHBox, RBHButton, RBHText } from '../../../shared/components';
import styles from './styles';
import './styles.css';
import CreateReportModal from './components/CreateReportModal';
import RequestList, { CustomReportItem, ListMeta } from './components/RequestList';
import { PPVFont, textStyles } from '../../../shared/theme/fonts-ppv';
import { Colors } from '../../../shared/theme';
import { CUSTOM_REPORT_ALERT_DURATION_MS, CUSTOM_REPORT_REQUEST_ID_LIST } from './redux/custom-report-constant';
import { customReportSagas } from './redux/custom-report-saga';
import { CustomReportService } from './redux/custom-report-service';

type AlertStatus = 'success' | 'error' | null;

const DEFAULT_META: ListMeta = { totalItems: 0, currentPage: 1, itemsPerPage: 10 };

export const CustomReportScreen: React.FC = () => {
    const dispatch = useDispatch();
    const [alertStatus, setAlertStatus] = useState<AlertStatus>(null);
    const [reportTypeFilter, setReportTypeFilter] = useState<string[]>([]);
    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [listData, setListData] = useState<CustomReportItem[]>([]);
    const [listMeta, setListMeta] = useState<ListMeta>(DEFAULT_META);
    const [listError, setListError] = useState(false);

    const fetchList = useCallback((params: { page: number; pageSize: number; reportType?: string[]; status?: string[] }) => {
        const serviceParams: Record<string, any> = { page: params.page, limit: params.pageSize };
        if (params.reportType && params.reportType.length > 0) serviceParams.reportType = params.reportType;
        if (params.status && params.status.length > 0) serviceParams.status = params.status;

        dispatch(httpCreators.request({
            id: CUSTOM_REPORT_REQUEST_ID_LIST.GET_CUSTOM_REPORTS,
            sagaHandler: customReportSagas.getCustomReportsHandler,
            service: CustomReportService.getCustomReports,
            serviceParams,
            callbackParams: {
                onSuccess: (data: any) => {
                    setListData(data.items ?? []);
                    setListMeta(data.meta ?? DEFAULT_META);
                },
                onFailure: () => { setListError(true); },
            },
        }));
    }, [dispatch]);

    useEffect(() => {
        fetchList({ page: 1, pageSize: 10, reportType: reportTypeFilter, status: statusFilter });
    }, [fetchList, reportTypeFilter, statusFilter]);

    const handleReportTypeChange = (value: string[]) => {
        setReportTypeFilter(value);
    };

    const handleStatusChange = (value: string[]) => {
        setStatusFilter(value);
    };

    const handleClearFilter = () => {
        setReportTypeFilter([]);
        setStatusFilter([]);
    };

    const handlePageChange = (pagination: any) => {
        fetchList({ page: pagination.current ?? 1, pageSize: pagination.pageSize ?? 10, reportType: reportTypeFilter, status: statusFilter });
    };

    return (
        <RBHBox style={styles.pageWrapper}>
            <PageHeader
                onSubmitSuccess={() => {
                    setAlertStatus('success');
                    fetchList({ page: 1, pageSize: 10, reportType: reportTypeFilter, status: statusFilter });
                }}
                onSubmitFailure={() => setAlertStatus('error')}
            />
            {alertStatus && (
                <div className="custom-report-alert-container" style={styles.alertContainer}>
                    <RBHAlert
                        type={alertStatus}
                        text={alertStatus === 'success'
                            ? 'main.custom-report.alert.success'
                            : 'main.custom-report.alert.error'
                        }
                        autoCloseDuration={CUSTOM_REPORT_ALERT_DURATION_MS}
                        afterClose={() => setAlertStatus(null)}
                    />
                </div>
            )}
            <RBHBox style={styles.defaultContent}>
                <RequestList
                    listData={listData}
                    listMeta={listMeta}
                    listError={listError}
                    onListErrorClose={() => setListError(false)}
                    reportTypeFilter={reportTypeFilter}
                    statusFilter={statusFilter}
                    onReportTypeChange={handleReportTypeChange}
                    onStatusChange={handleStatusChange}
                    onClearFilter={handleClearFilter}
                    onPageChange={handlePageChange}
                />
            </RBHBox>
        </RBHBox>
    );
};

interface PageHeaderProps {
    onSubmitSuccess: () => void;
    onSubmitFailure: () => void;
}

const PageHeader: React.FC<PageHeaderProps> = ({ onSubmitSuccess, onSubmitFailure }) => {
    const [modalVisible, setModalVisible] = useState(false);

    return (
        <>
            <RBHBox style={styles.header} className="custom-report-header">
                <RBHBox style={styles.titleSection}>
                    <RBHText
                        data-testid="custom-report-title"
                        id="custom-report-title"
                        style={textStyles.h2}
                        text="main.custom-report.title"
                    />
                    <RBHText
                        data-testid="custom-report-subtitle"
                        id="custom-report-subtitle"
                        style={{...textStyles.subtitle1, color: Colors.secondary, fontFamily: PPVFont.Regular}}
                        text="main.custom-report.subtitle"
                    />
                </RBHBox>
                <RBHButton
                    data-testid="custom-report-create-btn"
                    id="custom-report-create-btn"
                    className="custom-report-create-btn"
                    style={{...textStyles.button1, ...styles.createButton}}
                    text="main.custom-report.create-btn"
                    type="primary"
                    icon={<PlusOutlined translate={null} />}
                    onClick={() => setModalVisible(true)}
                />
            </RBHBox>
            <CreateReportModal
                visible={modalVisible}
                onClose={() => setModalVisible(false)}
                onSuccess={onSubmitSuccess}
                onFailure={onSubmitFailure}
            />
        </>
    );
};

export default CustomReportScreen;
